import React, { memo, useState, useEffect } from "react";
import { toast } from "react-toastify";
import {
  WizardContainer,
  WizardHeader,
  WizardTitle,
  StepIndicator,
  WizardProgress,
  ProgressBar,
  ProgressFill,
  StepContent,
  StepTitle,
  StepDescription,
  Button,
  ButtonGroup,
  FormGroup,
  Label,
  Input,
  Select,
  TextArea,
  ComponentMappingContainer,
  ComponentRow,
  ComponentName,
  VersionSelect,
  LoadingSpinner,
  ErrorMessage,
  TypeBadge,
  TagFilterRow,
  TagFilterInput,
  TagFilterClear,
} from "./styles";
import API from "Utils/api";
import { otaLogger } from "../../logger";

const log = otaLogger.byPrefix("ReleaseWizard");

interface VehicleType {
  id: string;
  name: string;
  model: string;
  manufacturer: string;
}

interface Release {
  id: string;
  name: string;
  vehicleTypeName: string;
  componentCount: number;
  status: string;
}

interface ComponentVersion {
  id: string;
  version_number: string;
  s3_uri: string | null;
  ccm_image_tag: string | null;
  description: string | null;
  tags: string[];
}

// Self-contained row: versions and base-release info live here so the
// version-change handler can use the array index — no separate ID lookup needed.
interface ComponentMapping {
  componentId: string;
  componentName: string;
  componentType: string;
  versions: ComponentVersion[];
  selectedVersionId: string;
  selectedVersionNumber: string;
  baseVersionNumber: string; // "" when no base release was provided
}

interface ReleaseWizardProps {
  onClose: () => void;
  onSuccess: () => void;
}

export const ReleaseWizard = memo<ReleaseWizardProps>(
  ({ onClose, onSuccess }) => {
    const [currentStep, setCurrentStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // ── Step 1 fields (collected locally — nothing written to DB) ──────────────
    const [releaseName, setReleaseName] = useState("");
    const [description, setDescription] = useState("");
    const [vehicleTypes, setVehicleTypes] = useState<VehicleType[]>([]);
    const [selectedVehicleType, setSelectedVehicleType] = useState("");
    const [releases, setReleases] = useState<Release[]>([]);
    const [selectedBaseRelease, setSelectedBaseRelease] = useState("");
    const [releaseVersion, setReleaseVersion] = useState("");
    const [configTarget, setConfigTarget] = useState("");
    const [configCommit, setConfigCommit] = useState("");
    const [configTime, setConfigTime] = useState("");
    const [configServiceTag, setConfigServiceTag] = useState("");
    const [ccmAutonomy, setCcmAutonomy] = useState("");
    const [ccmConfig, setCcmConfig] = useState("");

    // ── Step 2 state ──────────────────────────────────────────────────────────
    const [componentMappings, setComponentMappings] = useState<
      ComponentMapping[]
    >([]);
    const [tagFilter, setTagFilter] = useState("");

    useEffect(() => {
      fetchVehicleTypes();
    }, []);

    useEffect(() => {
      if (selectedVehicleType) {
        fetchReleases(selectedVehicleType);
      }
    }, [selectedVehicleType]);

    const fetchVehicleTypes = async () => {
      try {
        const response = await API.get("/ota/ota-vehicle-types/");
        setVehicleTypes(response.data?.results || response.data || []);
      } catch (err: any) {
        toast.error("Failed to load vehicle types");
        log.error("Failed to load vehicle types", err as Record<string, any>);
      }
    };

    const fetchReleases = async (vehicleTypeId: string) => {
      try {
        const response = await API.get("/ota/releases/", {
          params: { vehicle_type: vehicleTypeId },
        });
        setReleases(response.data?.results || response.data || []);
      } catch (err: any) {
        log.error("Failed to load releases", err as Record<string, any>);
      }
    };

    // Loads components from vehicle-component-links + versions, without creating
    // any release record in the DB. Base release pre-fill is done here too.
    const fetchComponentsForStep2 = async (
      vehicleTypeId: string,
      baseReleaseId: string
    ) => {
      setLoading(true);
      setError(null);
      try {
        // 1. Linked components for this vehicle type
        const linksResp = await API.get("/ota/vehicle-component-links/", {
          params: { vehicle_type_id: vehicleTypeId, page_size: 200 },
        });
        const rawLinks: any[] = linksResp.data?.results ?? linksResp.data ?? [];
        if (!Array.isArray(rawLinks) || rawLinks.length === 0) {
          setComponentMappings([]);
          return;
        }

        // 2. Base release component versions (for pre-fill), keyed by component name
        const baseByName: Record<string, string> = {};
        if (baseReleaseId) {
          try {
            const baseResp = await API.get("/ota/release-components/", {
              params: { release_id: baseReleaseId, page_size: 200 },
            });
            const rawBase: any[] =
              baseResp.data?.results ?? baseResp.data ?? [];
            if (Array.isArray(rawBase)) {
              rawBase.forEach((b: any) => {
                // API returns snake_case; also accept camelCase just in case
                const bName: string =
                  b.component_name ?? b.componentName ?? b.name ?? "";
                const bVer: string = b.version_number ?? b.versionNumber ?? "";
                if (bName && bVer) baseByName[bName] = String(bVer);
              });
            }
          } catch (err) {
            log.warn(
              "Failed to fetch base release components",
              err as Record<string, any>
            );
          }
        }

        // 3. For each link, fetch its available versions
        const mappings: ComponentMapping[] = await Promise.all(
          rawLinks.map(async (link: any) => {
            // Normalize field names — API returns component_id and component_name
            // (snake_case). Accept name/type as fallback for older variants.
            const componentId = String(
              link.component_id ?? link.componentId ?? ""
            );
            const componentName = String(
              link.component_name ?? link.componentName ?? link.name ?? ""
            );
            const componentType = String(
              link.component_type ?? link.componentType ?? link.type ?? ""
            );

            let versions: ComponentVersion[] = [];
            if (componentId) {
              try {
                const verResp = await API.get("/ota/versions/", {
                  params: { component_id: componentId, page_size: 200 },
                });
                const rawVers: any[] =
                  verResp.data?.results ?? verResp.data ?? [];
                if (Array.isArray(rawVers)) {
                  versions = rawVers.map((v: any) => ({
                    id: String(v.id ?? ""),
                    version_number: String(
                      v.version_number ?? v.versionNumber ?? ""
                    ),
                    s3_uri: v.s3_uri ?? v.s3Uri ?? null,
                    ccm_image_tag: v.ccm_image_tag ?? v.ccmImageTag ?? null,
                    description: v.description ?? null,
                    tags: Array.isArray(v.tags) ? v.tags : [],
                  }));
                }
              } catch {
                // non-fatal: component shows with "No versions available"
              }
            }

            // Pre-fill from base release when version numbers match
            const baseVer = baseByName[componentName] ?? "";
            let selectedVersionId = "";
            let selectedVersionNumber = "";
            if (baseVer) {
              const match = versions.find(
                (v) => String(v.version_number) === String(baseVer)
              );
              if (match) {
                selectedVersionId = match.id;
                selectedVersionNumber = match.version_number;
              }
            }

            return {
              componentId,
              componentName,
              componentType,
              versions,
              selectedVersionId,
              selectedVersionNumber,
              baseVersionNumber: baseVer,
            };
          })
        );

        setComponentMappings(mappings);
      } catch (err: any) {
        toast.error("Failed to load components");
        log.error("Failed to load components", err as Record<string, any>);
      } finally {
        setLoading(false);
      }
    };

    const handleStep1Next = async () => {
      if (!releaseName.trim()) {
        toast.error("Please enter a release name");
        return;
      }
      if (!selectedVehicleType) {
        toast.error("Please select a vehicle type");
        return;
      }
      if (!ccmAutonomy.trim()) {
        toast.error("Please enter the CCM Autonomy identifier");
        return;
      }
      if (!ccmConfig.trim()) {
        toast.error("Please enter the CCM Config identifier");
        return;
      }
      await fetchComponentsForStep2(selectedVehicleType, selectedBaseRelease);
      setCurrentStep(2);
    };

    // Update a single component row by its array index — immune to duplicate
    // or empty componentId values since the index is always unique.
    const handleVersionChange = (
      index: number,
      versionId: string,
      versionNumber: string
    ) => {
      setComponentMappings((prev) =>
        prev.map((m, i) =>
          i === index
            ? {
                ...m,
                selectedVersionId: versionId,
                selectedVersionNumber: versionNumber,
              }
            : m
        )
      );
    };

    // Build JSON manifest locally and submit atomically via import-release —
    // same path as the file-upload flow, no DB record created before this call.
    const handleCreateRelease = async () => {
      const missing = componentMappings.filter((m) => !m.selectedVersionId);
      if (missing.length > 0) {
        toast.error(
          `Please select versions for all components. Missing: ${missing
            .map((m) => m.componentName)
            .join(", ")}`
        );
        return;
      }

      const vehicleType = vehicleTypes.find(
        (vt) => vt.id === selectedVehicleType
      );
      if (!vehicleType) {
        toast.error("Selected vehicle type not found");
        return;
      }

      const manifest: Record<string, any> = {
        name: releaseName.trim(),
        vehicle_type: vehicleType.name,
        ccm_autonomy: ccmAutonomy.trim(),
        ccm_config: ccmConfig.trim(),
        components: componentMappings.map((m) => ({
          name: m.componentName,
          version: m.selectedVersionNumber,
        })),
      };
      if (description.trim()) manifest.description = description.trim();
      if (releaseVersion.trim())
        manifest.release_version = releaseVersion.trim();
      if (configTarget.trim()) manifest.config_target = configTarget.trim();
      if (configCommit.trim()) manifest.config_commit = configCommit.trim();
      if (configTime) manifest.config_time = parseInt(configTime, 10);
      if (configServiceTag.trim())
        manifest.config_service_tag = configServiceTag.trim();

      const blob = new Blob([JSON.stringify(manifest)], {
        type: "application/json",
      });
      const file = new File([blob], "release.json", {
        type: "application/json",
      });
      const formData = new FormData();
      formData.append("file", file);

      try {
        setLoading(true);
        setError(null);
        await API.post("/ota/releases/import-release/", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Release created successfully!");
        onSuccess();
        onClose();
      } catch (err: any) {
        const msg =
          err.response?.data?.error ||
          err.response?.data?.message ||
          "Failed to create release";
        setError(msg);
        toast.error(msg);
      } finally {
        setLoading(false);
      }
    };

    // ── Rendering ──────────────────────────────────────────────────────────────

    const renderStep1 = () => (
      <StepContent>
        <div>
          <StepTitle>Step 1: Setup</StepTitle>
          <StepDescription>
            Name your release and select a vehicle type
          </StepDescription>
        </div>

        <FormGroup>
          <Label>Release Name *</Label>
          <Input
            type="text"
            value={releaseName}
            onChange={(e) => setReleaseName(e.target.value)}
            placeholder="e.g., Winter 2026 Release"
          />
        </FormGroup>

        <FormGroup>
          <Label>Description</Label>
          <TextArea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Release notes and description..."
            rows={3}
          />
        </FormGroup>

        <FormGroup>
          <Label>Vehicle Type *</Label>
          <Select
            value={selectedVehicleType}
            onChange={(e) => setSelectedVehicleType(e.target.value)}
          >
            <option value="">Select vehicle type...</option>
            {vehicleTypes.map((vt) => (
              <option key={vt.id} value={vt.id}>
                {vt.name} ({vt.manufacturer} {vt.model})
              </option>
            ))}
          </Select>
        </FormGroup>

        <FormGroup>
          <Label>Base Release (Optional)</Label>
          <Select
            value={selectedBaseRelease}
            onChange={(e) => setSelectedBaseRelease(e.target.value)}
            disabled={!selectedVehicleType}
          >
            <option value="">None (start fresh)</option>
            {releases.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name} ({r.componentCount} components)
              </option>
            ))}
          </Select>
        </FormGroup>

        <FormGroup>
          <Label>Release Version</Label>
          <Input
            type="text"
            value={releaseVersion}
            onChange={(e) => setReleaseVersion(e.target.value)}
            placeholder="10.2.6-20240605"
          />
        </FormGroup>

        <FormGroup>
          <Label>Config Target (branch/tag)</Label>
          <Input
            type="text"
            value={configTarget}
            onChange={(e) => setConfigTarget(e.target.value)}
            placeholder="master"
          />
        </FormGroup>

        <FormGroup>
          <Label>Config Commit (SHA)</Label>
          <Input
            type="text"
            value={configCommit}
            onChange={(e) => setConfigCommit(e.target.value)}
            placeholder="fe98c1eea2c546f980081965aafeeb409872305a"
          />
        </FormGroup>

        <FormGroup>
          <Label>Config Time (Unix timestamp)</Label>
          <Input
            type="number"
            value={configTime}
            onChange={(e) => setConfigTime(e.target.value)}
            placeholder="1774286584"
          />
        </FormGroup>

        <FormGroup>
          <Label>Config Service Tag</Label>
          <Input
            type="text"
            value={configServiceTag}
            onChange={(e) => setConfigServiceTag(e.target.value)}
            placeholder="CS1b6f5b29"
          />
        </FormGroup>

        <FormGroup>
          <Label>CCM Autonomy *</Label>
          <Input
            type="text"
            value={ccmAutonomy}
            onChange={(e) => setCcmAutonomy(e.target.value)}
            placeholder="e.g. 10.8"
          />
        </FormGroup>

        <FormGroup>
          <Label>CCM Config *</Label>
          <Input
            type="text"
            value={ccmConfig}
            onChange={(e) => setCcmConfig(e.target.value)}
            placeholder="e.g. standard"
          />
        </FormGroup>

        {error && <ErrorMessage>{error}</ErrorMessage>}

        <ButtonGroup>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleStep1Next}
            disabled={loading}
          >
            {loading ? "Loading..." : "Next →"}
          </Button>
        </ButtonGroup>
      </StepContent>
    );

    const renderStep2 = () => {
      const vehicleType = vehicleTypes.find(
        (vt) => vt.id === selectedVehicleType
      );
      const hasBase = componentMappings.some((m) => !!m.baseVersionNumber);

      return (
        <StepContent>
          <div>
            <StepTitle>Step 2: Component Mapping</StepTitle>
            {vehicleType && (
              <StepDescription>
                {vehicleType.name} ({vehicleType.manufacturer}{" "}
                {vehicleType.model}){" — "}
                {componentMappings.length} component
                {componentMappings.length !== 1 ? "s" : ""} linked to this
                vehicle type
              </StepDescription>
            )}
          </div>

          <TagFilterRow>
            <TagFilterInput
              type="text"
              placeholder="Filter versions by tag (e.g. stable, june-sprint)…"
              value={tagFilter}
              onChange={(e) => setTagFilter(e.target.value)}
            />
            {tagFilter && (
              <TagFilterClear onClick={() => setTagFilter("")}>
                ✕
              </TagFilterClear>
            )}
          </TagFilterRow>

          <ComponentMappingContainer>
            {loading ? (
              <LoadingSpinner>Loading components...</LoadingSpinner>
            ) : componentMappings.length === 0 ? (
              <div
                style={{ padding: "20px", textAlign: "center", color: "#999" }}
              >
                No components linked to this vehicle type yet.
                <br />
                Please configure component links in the Management tab first.
              </div>
            ) : (
              componentMappings.map((mapping, idx) => {
                const isContainer =
                  mapping.componentType.toLowerCase() === "container";

                // Delta badge relative to base release
                let deltaBadge: React.ReactNode = null;
                if (hasBase) {
                  if (!mapping.baseVersionNumber) {
                    deltaBadge = (
                      <span
                        style={{
                          fontSize: 11,
                          padding: "2px 7px",
                          borderRadius: 3,
                          background: "#e6f4ea",
                          color: "#276749",
                          fontWeight: 600,
                          marginLeft: 8,
                        }}
                      >
                        New
                      </span>
                    );
                  } else if (
                    mapping.selectedVersionNumber &&
                    mapping.selectedVersionNumber !== mapping.baseVersionNumber
                  ) {
                    deltaBadge = (
                      <span
                        style={{
                          fontSize: 11,
                          padding: "2px 7px",
                          borderRadius: 3,
                          background: "#e6f4ea",
                          color: "#276749",
                          fontWeight: 600,
                          marginLeft: 8,
                        }}
                      >
                        v{mapping.baseVersionNumber} → v
                        {mapping.selectedVersionNumber}
                      </span>
                    );
                  } else if (
                    mapping.selectedVersionNumber &&
                    mapping.selectedVersionNumber === mapping.baseVersionNumber
                  ) {
                    deltaBadge = (
                      <span
                        style={{
                          fontSize: 11,
                          padding: "2px 7px",
                          borderRadius: 3,
                          background: "#f3f4f6",
                          color: "#6b7280",
                          marginLeft: 8,
                        }}
                      >
                        Unchanged
                      </span>
                    );
                  }
                }

                return (
                  <ComponentRow key={mapping.componentId || String(idx)}>
                    <ComponentName>
                      {mapping.componentName}
                      {mapping.componentType && (
                        <TypeBadge
                          componentType={
                            mapping.componentType.charAt(0).toUpperCase() +
                            mapping.componentType.slice(1)
                          }
                          style={{ marginLeft: 8 }}
                        >
                          {mapping.componentType}
                        </TypeBadge>
                      )}
                      {deltaBadge}
                    </ComponentName>

                    <VersionSelect
                      value={mapping.selectedVersionId}
                      onChange={(e) => {
                        const chosen = mapping.versions.find(
                          (v) => v.id === e.target.value
                        );
                        handleVersionChange(
                          idx,
                          e.target.value,
                          chosen?.version_number ?? ""
                        );
                      }}
                      disabled={mapping.versions.length === 0}
                    >
                      <option value="">
                        {mapping.versions.length === 0
                          ? "No versions available"
                          : "Select version..."}
                      </option>
                      {mapping.versions
                        .filter((v) => {
                          if (!tagFilter.trim()) return true;
                          const needle = tagFilter.trim().toLowerCase();
                          return (
                            v.tags.some((t) =>
                              t.toLowerCase().includes(needle)
                            ) || v.id === mapping.selectedVersionId
                          );
                        })
                        .map((v) => {
                          const uri = v.s3_uri ?? "";
                          const s3Suffix =
                            uri.match(/^s3:\/\/[^/]+\/(.+)$/)?.[1] ?? "";
                          const subtitle = isContainer
                            ? v.ccm_image_tag ?? ""
                            : s3Suffix;
                          const tagsLabel =
                            v.tags.length > 0 ? ` [${v.tags.join(", ")}]` : "";
                          const desc = v.description
                            ? ` · ${v.description}`
                            : "";
                          return (
                            <option key={v.id} value={v.id}>
                              v{v.version_number}
                              {subtitle ? ` — ${subtitle}` : ""}
                              {tagsLabel}
                              {desc}
                            </option>
                          );
                        })}
                    </VersionSelect>
                  </ComponentRow>
                );
              })
            )}
          </ComponentMappingContainer>

          {error && <ErrorMessage>{error}</ErrorMessage>}

          <ButtonGroup>
            <Button variant="secondary" onClick={() => setCurrentStep(1)}>
              ← Back
            </Button>
            <Button
              variant="primary"
              onClick={handleCreateRelease}
              disabled={loading}
            >
              {loading ? "Creating..." : "Create Release"}
            </Button>
          </ButtonGroup>
        </StepContent>
      );
    };

    return (
      <WizardContainer>
        <WizardHeader>
          <WizardTitle>Release Wizard</WizardTitle>
          <StepIndicator>Step {currentStep} of 2</StepIndicator>
        </WizardHeader>

        <WizardProgress>
          <ProgressBar>
            <ProgressFill style={{ width: `${(currentStep / 2) * 100}%` }} />
          </ProgressBar>
        </WizardProgress>

        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
      </WizardContainer>
    );
  }
);
